/**
 * Guardas de ruta. **Todas son cosméticas** (Arquitectura §3.2): lo que protege los datos
 * es la API, que valida rol + sede asignada + sede activa y audita el intento denegado.
 * Acá se evita mostrar una pantalla que va a rebotar, nada más.
 */
import { useEffect, type ReactNode } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import type { UserRole } from "@viva/shared";
import { useAuth } from "../../lib/auth/index.js";
import { SURFACE_BY_AREA, setSurface, type Area } from "../../lib/auth/index.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { canSeeScreen } from "../nav.js";
import { SCREENS } from "../screens.js";
import Forbidden from "../pages/Forbidden.js";

/** Pantalla de carga del arranque (mientras `GET /api/me` confirma la sesión). */
export function BootScreen() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-night">
      <div className="flex w-[280px] flex-col gap-3">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-24" />
        <Skeleton className="h-4 w-28" />
      </div>
    </div>
  );
}

/** Fija la superficie del área: viaja en `X-Surface` y la guarda la auditoría (RN-ROL-10). */
export function SurfaceScope({ area, children }: { area: Area; children: ReactNode }) {
  useEffect(() => {
    setSurface(SURFACE_BY_AREA[area]);
  }, [area]);
  return <>{children}</>;
}

export function RequireAuth() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <BootScreen />;
  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <Outlet />;
}

/**
 * RN-ROL-08: un usuario con más de una sede elige la activa **al entrar**. Sin sede activa
 * no se opera: cada operación se imputa a una sede (D6).
 */
export function RequireActiveBranch() {
  const { needsBranchChoice } = useAuth();
  const location = useLocation();

  if (needsBranchChoice && location.pathname !== "/admin/sede-activa") {
    return <Navigate to="/admin/sede-activa" replace state={{ from: location.pathname }} />;
  }
  return <Outlet />;
}

export function RequireRole({ roles }: { roles: UserRole[] }) {
  const { user } = useAuth();
  if (!user) return <Forbidden />;
  return roles.includes(user.role) ? <Outlet /> : <Forbidden />;
}

/** Guarda por pantalla: usa la declaración de `screens.ts` (una sola fuente de permisos). */
export function RequireScreen({ screenId, children }: { screenId: string; children: ReactNode }) {
  const { user } = useAuth();
  const screen = SCREENS.find((candidate) => candidate.id === screenId);
  if (!user || !screen) return <Forbidden />;
  return canSeeScreen(screen, user.role) ? <>{children}</> : <Forbidden what={screen.title} />;
}
